import { useEffect, useRef } from 'react';
import { useHintBar } from './useHintBar';

/** Event handler type matching EventDispatcher from potree_fork */
type EventHandler = (event: Record<string, unknown>) => void;

/** Listener type matching EventDispatcher from potree_fork */
type Listener = (event: { type: string; [key: string]: unknown }) => void;

/**
 * QuestRegistry interface matching the runtime shape from potree_fork.
 */
interface QuestRegistryLike {
  resolveTooltipKey(toolName: string, phase: string | undefined): string | undefined;
  resolveOverrideKey(toolName: string, overrideId: string): string | undefined;
  getOverrideKeys(toolName: string): Record<string, string>;
  hasQuest(toolName: string): boolean;
}

/** TranslationService interface matching the runtime shape from potree_fork */
interface TranslationServiceLike {
  t(source: string, options?: { swc?: boolean }): string;
  addEventListener(type: string, handler: Listener): void;
  removeEventListener(type: string, handler: Listener): void;
}

/**
 * Viewer shape with scene, personaManager, and translationService.
 */
interface ViewerLike {
  scene: {
    addEventListener(type: string, handler: EventHandler): void;
    removeEventListener(type: string, handler: EventHandler): void;
  };
  personaManager: {
    questRegistry: QuestRegistryLike;
  };
  translationService: TranslationServiceLike;
}

/**
 * useQuestHintBar - Hook that listens for tool state changes and updates the HintBar
 *
 * Subscribes to tool_state_changed, tool_disabled, and tool_hint_override events.
 * When a tool dispatches a phase change, the quest registry resolves it to a
 * translation source string, which is translated via the viewer's TranslationService.
 *
 * Hint overrides (e.g., from modifier keys or hover) temporarily take priority
 * over phase-based hints. When an override is popped, the last known phase hint
 * is automatically restored.
 *
 * @param viewer - The Potree viewer instance (may be undefined during init)
 */
export function useQuestHintBar(viewer: ViewerLike | undefined): void {
  const { showHint, hideHint } = useHintBar();

  // Use refs for callbacks to avoid re-subscribing on every render
  const showHintRef = useRef(showHint);
  const hideHintRef = useRef(hideHint);

  useEffect(() => {
    showHintRef.current = showHint;
    hideHintRef.current = hideHint;
  });

  // Track last phase for restoring after override pop
  const lastToolRef = useRef<string | undefined>(undefined);
  const lastPhaseRef = useRef<string | undefined>(undefined);
  const overrideActiveRef = useRef(false);

  useEffect(() => {
    if (!viewer?.scene || !viewer?.personaManager?.questRegistry || !viewer?.translationService) return;

    const questRegistry = viewer.personaManager.questRegistry;
    const ts = viewer.translationService;

    /** Translate a source string with "start with capital" */
    const translate = (source: string): string => ts.t(source, { swc: true });

    const handleStateChanged: EventHandler = (event) => {
      const tool = event.tool as { name: string } | undefined;
      const state = event.state as { phase: string } | undefined;
      if (!tool || !state) return;

      // Always remember the latest phase for override pop fallback
      lastToolRef.current = tool.name;
      lastPhaseRef.current = state.phase;

      // Don't overwrite active override with phase hint
      if (overrideActiveRef.current) return;

      const sourceString = questRegistry.resolveTooltipKey(tool.name, state.phase);
      if (sourceString) {
        showHintRef.current(translate(sourceString));
      }
    };

    const handleHintOverride: EventHandler = (event) => {
      const tool = event.tool as { name: string } | undefined;
      const override = event.override as string | null;
      if (!tool) return;

      if (override) {
        // Push: resolve override ID to translation source string via registry
        overrideActiveRef.current = true;
        const sourceString = questRegistry.resolveOverrideKey(tool.name, override);
        if (sourceString) {
          showHintRef.current(translate(sourceString));
        }
      } else {
        // Pop: revert to last known phase hint
        overrideActiveRef.current = false;
        if (lastToolRef.current && lastPhaseRef.current !== undefined) {
          const sourceString = questRegistry.resolveTooltipKey(lastToolRef.current, lastPhaseRef.current);
          if (sourceString) {
            showHintRef.current(translate(sourceString));
          }
        }
      }
    };

    const handleToolDisabled: EventHandler = (event) => {
      const tool = event.tool as { name: string } | undefined;
      if (!tool) return;

      if (questRegistry.hasQuest(tool.name)) {
        lastToolRef.current = undefined;
        lastPhaseRef.current = undefined;
        overrideActiveRef.current = false;
        hideHintRef.current();
      }
    };

    // Re-translate currently displayed hint when language changes
    const handleLanguageChanged: Listener = () => {
      if (overrideActiveRef.current) return;
      if (lastToolRef.current && lastPhaseRef.current !== undefined) {
        const sourceString = questRegistry.resolveTooltipKey(lastToolRef.current, lastPhaseRef.current);
        if (sourceString) {
          showHintRef.current(translate(sourceString));
        }
      }
    };

    viewer.scene.addEventListener('tool_state_changed', handleStateChanged);
    viewer.scene.addEventListener('tool_hint_override', handleHintOverride);
    viewer.scene.addEventListener('tool_disabled', handleToolDisabled);
    ts.addEventListener('language_changed', handleLanguageChanged);

    return () => {
      viewer.scene.removeEventListener('tool_state_changed', handleStateChanged);
      viewer.scene.removeEventListener('tool_hint_override', handleHintOverride);
      viewer.scene.removeEventListener('tool_disabled', handleToolDisabled);
      ts.removeEventListener('language_changed', handleLanguageChanged);
    };
  }, [viewer]);
}
